import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import * as I from 'immutable';
import React from 'react';

import Gravatar from 'app/components/Gravatar';
import {usePushNotification} from 'app/components/Notification';
import {ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {OrgUsersActions} from 'app/providers/OrgUsersProvider';
import {recordEvent} from 'app/tools/Analytics';
import {USER_ROLE_OWNER} from 'app/utils/constants';
import {loginAppUrl} from 'app/utils/environmentUtils';
import {useStateWithDeps} from 'app/utils/hookUtils';
import * as inviteUtils from 'app/utils/inviteUtils';
import {isOwnerOrAdmin} from 'app/utils/userUtils';

import cs from './TeamMemberModal.module.scss';

export interface Props {
  editingUserId: string | null;
  organizationId: string;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  organizationUsers: ApiOrganizationUser[];
  actions: OrgUsersActions;
  onClose: () => void;
}

export const ROLE_MESSAGING: {[role in ApiOrganizationUser['role']]: string} = {
  readonly: 'Read-only accounts can view imagery and notes.',
  regular:
    'Member accounts can view imagery, add new properties and portfolios, make notes, and generate reports.',
  admin:
    'Admin accounts can order and view imagery, add new properties and portfolios, send invites to other users, change member permissions, edit notes, remove team members, make notes, archive properties, and generate reports.',
  owner:
    'Owner accounts can manage billing and subscriptions, reset passwords and multi-factor authentication for other users, order and view imagery, add new properties and portfolios, send invites to other users, change member permissions, edit notes, remove team members, make notes, archive properties, and generate reports.',
};

export const ROLE_SUPPORT = 'https://lens.tech/support/article/32-inviting-your-team';

const TeamMemberModal: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  profile,
  editingUserId,
  organizationId,
  organizationUsers,
  actions,
  onClose,
}) => {
  const isOpen = !!editingUserId;
  const organizationUser = organizationUsers.find((u) => u!.id === editingUserId)!;
  const userId = organizationUser?.id;
  const viewingUserIsOwnerOrAdmin = isOwnerOrAdmin(profile.get('role'));
  const viewingUserIsOwner = profile.get('role') === USER_ROLE_OWNER;
  const viewingUserIsSelf = profile.get('id') === userId;
  const isRoleSelectDisabled = !viewingUserIsOwnerOrAdmin || viewingUserIsSelf;
  const isDeleteDisabled = !viewingUserIsOwnerOrAdmin || viewingUserIsSelf;
  // Resetting MFA happens in the login app, which requires the requesting user
  // to be an owner of one of the target user's orgs (see backend user.reset_mfa).
  const isResetMfaDisabled = !viewingUserIsOwner;
  // Force logout also requires owner, for the same reason.
  const isForceLogoutDisabled = !viewingUserIsOwner;
  const pushNotification = usePushNotification();

  const [isUpdating, setIsUpdating] = React.useState(false);

  const [nameField, setNameField] = useStateWithDeps<string | undefined>(organizationUser?.name, [
    organizationUser,
  ]);
  const [roleField, setRoleField] = useStateWithDeps<ApiOrganizationUser['role'] | undefined>(
    organizationUser?.role,
    [organizationUser]
  );

  const onRoleSelectChange = React.useCallback(
    async (roleField) => {
      const currentRole = organizationUser.role;

      // Do nothing if we dont have a ref, or if role is current role
      if (!roleField || roleField === currentRole) {
        return;
      }

      // This is identical to our role select enabled check,
      //  but just in case something unexpected happens, guard
      if (!viewingUserIsOwnerOrAdmin || viewingUserIsSelf) {
        return;
      }

      // Check if valid role and isn't current role
      if (!inviteUtils.ROLE_NAMES[roleField]) {
        return;
      }

      // Owner promotion requires reauthentication via the login app
      if (roleField === USER_ROLE_OWNER) {
        if (
          confirm(
            'Are you sure you want to make this teammate an owner?\n\nOwners can manage account-level settings and security decisions, so we recommend limiting this role to only the people who need it.'
          )
        ) {
          window.location.assign(
            `${loginAppUrl}/promoteToOwner?userId=${userId}&organizationId=${organizationId}&redirect=${window.location.href}`
          );
        } else {
          setRoleField(currentRole);
        }
        return;
      }

      try {
        setIsUpdating(true);
        await actions.setUserRole(userId, roleField);
        recordEvent('Set user role');
        pushNotification({
          message: `User role updated.`,
          autoHideDuration: 3000,
          options: {
            intent: B.Intent.SUCCESS,
          },
        });
      } catch (_e) {
        setRoleField(currentRole);
        pushNotification({
          message: "There was a problem updating user's role name. Please try again.",
          autoHideDuration: 3000,
          options: {
            intent: B.Intent.DANGER,
          },
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [
      actions,
      viewingUserIsOwnerOrAdmin,
      viewingUserIsSelf,
      organizationUser,
      organizationId,
      pushNotification,
      setRoleField,
      userId,
    ]
  );

  const onInputBlur = React.useCallback(async () => {
    const name = organizationUser.name;

    // Do nothing if we dont have a ref, or our value has not changed.
    if (!nameField || nameField === name) {
      return;
    }

    if (!nameField.trim()) {
      setNameField(name);
      return;
    }

    try {
      setIsUpdating(true);
      await actions.setUserName(userId, nameField);
      recordEvent('Set user name');
      pushNotification({
        message: `User name updated.`,
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.SUCCESS,
        },
      });
    } catch (_e) {
      // Reset input value and inform of failure
      setNameField(name);
      pushNotification({
        message: "There was a problem updating user's name. Please try again.",
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.DANGER,
        },
      });
    } finally {
      setIsUpdating(false);
    }
  }, [actions, nameField, organizationUser, pushNotification, setNameField, userId]);

  const onDelete = React.useCallback(async () => {
    const name = organizationUser.name;
    if (
      confirm(`Are you sure you want to remove ${organizationUser.name}? This cannot be undone.`)
    ) {
      try {
        setIsUpdating(true);
        await actions.deleteUser(userId);
        recordEvent('Deleted user');
        // Inform user of success and close modal
        pushNotification({
          message: `User ${name} deleted.`,
          autoHideDuration: 3000,
          options: {
            intent: B.Intent.SUCCESS,
          },
        });
        onClose();
      } catch (_e) {
        // Inform user of failure
        pushNotification({
          message: `There was a problem deleting ${name}. Please try again.`,
          autoHideDuration: 3000,
          options: {
            intent: B.Intent.DANGER,
          },
        });
      } finally {
        setIsUpdating(false);
      }
    }
  }, [actions, onClose, organizationUser, pushNotification, userId]);

  const onResetMfa = React.useCallback(() => {
    // Hand off to the login app, which reauthenticates the requesting user,
    // resets the target user's MFA, then redirects back here. We pass the org
    // currently being managed (which both this owner and the target user
    // belong to) so the login app can look up and display the target user's
    // name/email for confirmation.
    if (
      confirm(
        "Are you sure you want to reset this user's multi-factor authentication? Depending on your organization restrictions and their role, they may be required to set it back up upon login."
      )
    ) {
      recordEvent('Reset user MFA');
      window.location.assign(
        `${loginAppUrl}/resetMfa?userId=${userId}&orgId=${organizationId}&redirect=${window.location.href}`
      );
    }
  }, [userId, organizationId]);

  const onForceLogout = React.useCallback(() => {
    if (
      confirm(
        "Are you sure you want to reset this user's credentials? They will be logged out immediately and will need to reset their password before logging back in."
      )
    ) {
      recordEvent('Force reset credentials');
      // Pass the org currently being managed so the login app can look up and
      // display the target user's name/email for confirmation (see onResetMfa).
      window.location.assign(
        `${loginAppUrl}/credentialsReset?userId=${userId}&orgId=${organizationId}&redirect=${window.location.href}`
      );
    }
  }, [userId, organizationId]);

  // Our editingUserId isnt in the organization, return null
  if (!organizationUser) {
    return null;
  }

  return (
    <B.Dialog isOpen={isOpen} onClose={onClose} className={cs.dialog}>
      <div className={cs.header}>
        <B.Button minimal tabIndex={0} onClick={() => onClose()}>
          <B.Icon icon="cross" size={32} />
        </B.Button>
      </div>
      <div className={classnames(cs.section, cs.pullUp)}>
        <div className={cs.sectionHead}>
          <h1>Profile</h1>
        </div>
        <div className={cs.sectionControls}>
          <div className={cs.stacked}>
            <div className={cs.field}>
              <label>Photo</label>
              <Gravatar email={organizationUser.email} />
            </div>
            <div className={cs.inline}>
              <div className={cs.field}>
                <label htmlFor="name-input">Name</label>
                <B.InputGroup
                  id="name-input"
                  disabled={isUpdating}
                  value={nameField}
                  onValueChange={(val) => setNameField(val)}
                  onBlur={onInputBlur}
                />
              </div>
              <div className={cs.field}>
                <label htmlFor="email">Email</label>
                <div className={cs.email}>{organizationUser.email}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={cs.section}>
        <div className={cs.sectionHead}>
          <h1>Account</h1>
        </div>
        <div className={cs.sectionControls}>
          <div className={cs.inline}>
            <div className={cs.field}>
              <label htmlFor="role-select">Role</label>

              <B.HTMLSelect
                id="role-select"
                disabled={isRoleSelectDisabled || isUpdating}
                onChange={(e) => onRoleSelectChange(e.currentTarget.value)}
                value={roleField}
                aria-label={`Change role for ${organizationUser.email}`}
              >
                {Object.keys(inviteUtils.ROLE_NAMES)
                  .filter((role) => role !== USER_ROLE_OWNER)
                  .map((role) => (
                    <option key={role} value={role}>
                      {inviteUtils.ROLE_NAMES[role]}
                    </option>
                  ))}
                {(viewingUserIsOwner || roleField === USER_ROLE_OWNER) && (
                  <option value={USER_ROLE_OWNER}>Owner</option>
                )}
              </B.HTMLSelect>
            </div>
            <div className={cs.description}>
              <div>{ROLE_MESSAGING[organizationUser.role]}</div>
              <a target="_blank" rel="noopener noreferrer" href={ROLE_SUPPORT}>
                Learn more about role permissions here.
              </a>
            </div>
          </div>
        </div>
      </div>
      {viewingUserIsOwnerOrAdmin && (
        <div className={cs.section}>
          <div className={cs.sectionHead}>
            <h1>Permissions and Security</h1>
            <div>Actions in this section may not be reversible. Use caution.</div>
          </div>
          <div className={cs.sectionControls}>
            <div className={cs.stacked}>
              <div className={cs.inline}>
                <div>
                  <B.Button disabled={isResetMfaDisabled || isUpdating} onClick={onResetMfa}>
                    Reset MFA
                  </B.Button>
                </div>
                <div>
                  {isResetMfaDisabled
                    ? 'Only owners can reset multi-factor authentication.'
                    : 'Reset this user’s multi-factor authentication. This will prompt the user to set up MFA again the next time they log in.'}
                </div>
              </div>
              <div className={cs.inline}>
                <div>
                  <B.Button
                    disabled={isForceLogoutDisabled || isUpdating || viewingUserIsSelf}
                    onClick={onForceLogout}
                  >
                    Log out user and reset password
                  </B.Button>
                </div>
                <div>
                  {isForceLogoutDisabled
                    ? 'Only owners can force a user logout.'
                    : 'Immediately log this user out. This will also reset their password if they use one.'}
                </div>
              </div>
              <div className={cs.inline}>
                <div>
                  <B.Button
                    disabled={isDeleteDisabled || isUpdating}
                    intent={B.Intent.DANGER}
                    onClick={onDelete}
                  >
                    Delete user
                  </B.Button>
                </div>

                <div>
                  Permanently delete this user.{' '}
                  {viewingUserIsSelf && 'Note: Users may not delete themselves.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </B.Dialog>
  );
};

export default TeamMemberModal;
